import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  LinearProgress,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { purchaseOrderImportService } from '../services'
import ImportSessionStepper from '../components/purchase-order-import/ImportSessionStepper'
import ImportSessionStatusChip from '../components/purchase-order-import/ImportSessionStatusChip'

const POLL_MS = 5000

const PurchaseOrderImportSessionPage = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSession = useCallback(async () => {
    try {
      const data = await purchaseOrderImportService.getSession(sessionId)
      setSession(data)
      setError(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load session')
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    fetchSession()
  }, [fetchSession])

  useEffect(() => {
    if (!session) return undefined
    const needsPoll = ['queued', 'parsing', 'resolving'].includes(session.status)
    if (!needsPoll) return undefined
    const id = setInterval(fetchSession, POLL_MS)
    return () => clearInterval(id)
  }, [session?.status, fetchSession])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !session) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Session not found'}</Alert>
      </Container>
    )
  }

  const progressPct =
    session.total_rows > 0
      ? Math.round((session.processed_rows / session.total_rows) * 100)
      : 0

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          navigate(
            `/orders/purchase/create/upload?orgId=${session.org_id}`,
          )
        }
        sx={{ mb: 2, color: '#666' }}
      >
        Back to upload
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Import progress
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ImportSessionStatusChip status={session.status} />
        {session.file_name && (
          <Typography variant="body2" color="text.secondary">
            {session.file_name}
          </Typography>
        )}
      </Box>

      <ImportSessionStepper status={session.status} />

      {session.error_summary && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {session.error_summary}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {session.processed_rows} of {session.total_rows} products verified
        </Typography>
        <LinearProgress variant="determinate" value={progressPct} sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Resolved: {session.resolved_rows} · Failed/warnings: {session.failed_rows}
        </Typography>
      </Box>

      {session.status === 'ready_for_review' && (
        <Button
          variant="contained"
          size="large"
          onClick={() =>
            navigate(`/orders/purchase/create/upload/${sessionId}/review`)
          }
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
        >
          Review &amp; create order
        </Button>
      )}

      {session.status === 'submitted' && session.created_order_id && (
        <Button
          variant="contained"
          onClick={() =>
            navigate(`/orders/${session.created_order_id}`, {
              state: { ordersListPath: '/orders/purchase' },
            })
          }
        >
          View order #{session.created_order_id}
        </Button>
      )}
    </Container>
  )
}

export default PurchaseOrderImportSessionPage
